import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { jobApiService } from '../../recruiter/services/jobApi';

interface JobReviewData {
    _id?: string;
    job_title: string;
    category?: string;
    organization_industry?: string;
    organization_level?: string;
    employment_type?: string | string[];
    location?: {
        country?: string;
        state?: string;
        city?: string;
    };
    remote?: boolean;
    salary_range?: any;
    min_age?: number;
    max_age?: number;
    gender?: string;
    experience_levels?: string[];
    required_experience_years?: number;
    job_responsibilities?: string[];
    languages?: Array<{ name: string; proficiency: string }>;
    software_skills?: Array<{ name: string; proficiency: string }>;
    requirements?: {
        soft_skills?: string[];
        required_skills?: string[];
        preferred_skills?: string[];
        education_level?: string;
        [key: string]: any;
    };
    job_description?: string;
    working_hours?: string;
    business_trips_required?: boolean;
    company_name: string;
}

const JobReview: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [jobData, setJobData] = useState<JobReviewData | null>(null);
    const [isPublishing, setIsPublishing] = useState(false);

    useEffect(() => {
        if (location.state?.jobData) {
            setJobData(location.state.jobData);
        } else {
            // If no job data, redirect back to job posting form
            navigate('/companies/job-posting');
        }
    }, [location.state, navigate]);

    const handleEdit = (section?: string) => {
        navigate('/companies/job-posting', {
            state: { jobData, scrollTo: section }
        });
    };

    const handlePublish = async () => {
        if (!jobData) return;

        try {
            setIsPublishing(true);

            // Get user data from localStorage
            const userDataString = localStorage.getItem('authUser');
            let recruiterId = '';
            let companyName = jobData.company_name;
            let companyId = '';

            if (userDataString) {
                try {
                    const userData = JSON.parse(userDataString);
                    // Backend now provides recruiter_id and company_name directly for company/recruiter users
                    recruiterId = userData.recruiter_id || userData.id || '';
                    companyName = userData.company_name || jobData.company_name;
                    companyId = userData.company_id || '';

                    console.log('User data for job posting:', {
                        recruiterId,
                        companyName,
                        companyId,
                        userRole: userData.userType || userData.role
                    });
                } catch (error) {
                    console.error('Failed to parse user data:', error);
                }
            }

            // Prepare data for API - format it properly for the backend
            // Format location as string for compatibility
            let locationString = '';
            if (jobData.location && jobData.location.city && jobData.location.country) {
                const parts = [
                    jobData.location.city,
                    jobData.location.state,
                    jobData.location.country
                ].filter(Boolean);
                locationString = parts.join(', ');
            }

            const jobPayload = {
                title: jobData.job_title,
                companyName: companyName,
                recruiterId: recruiterId,
                category: jobData.category,
                organizationIndustry: jobData.organization_industry,
                organizationLevel: jobData.organization_level,
                description: jobData.job_description,
                location: locationString || undefined,
                remote: jobData.remote,
                employmentType: Array.isArray(jobData.employment_type)
                    ? jobData.employment_type.join(', ')
                    : jobData.employment_type,
                salaryRange: typeof jobData.salary_range === 'object'
                    ? `$${jobData.salary_range.min?.toLocaleString()} - $${jobData.salary_range.max?.toLocaleString()}`
                    : jobData.salary_range,
                experienceLevel: jobData.experience_levels?.[0] || '',
                requirements: {
                    requiredSkills: jobData.requirements?.required_skills || [],
                    preferredSkills: jobData.requirements?.preferred_skills || [],
                    education: jobData.requirements?.education_level || '',
                    yearsExperience: jobData.required_experience_years || 0
                },
                isActive: true
            };

            console.log('Job payload being sent:', jobPayload);

            // Create job in MongoDB - this will trigger embedding generation
            const response = await jobApiService.createJob(jobPayload);
            console.log('Job published successfully:', response);

            // Navigate to success page with flow state
            navigate('/companies/job-success', {
                state: { jobData: response, fromJobPosting: true }
            });
        } catch (error) {
            console.error('Error publishing job:', error);
            alert('Failed to publish job. Please try again.');
        } finally {
            setIsPublishing(false);
        }
    };

    if (!jobData) {
        return null;
    }

    // Format employment types as array
    const employmentTypes = Array.isArray(jobData.employment_type)
        ? jobData.employment_type
        : typeof jobData.employment_type === 'string'
            ? jobData.employment_type.split(',').map(t => t.trim())
            : [];

    return (
        <CompanyLayout>
            {/* Back Button */}
            <button
                onClick={() => navigate('/companies/job-posting', { state: { jobData } })}
                className="mb-6 hover:opacity-70 transition-opacity"
            >
                <img src="/jobs/back_button.svg" alt="Back" className="w-6 h-6" />
            </button>

            {/* Header */}
            <div className="mb-6">
                <h1 className="text-[28px] font-semibold text-[#282828]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Post New Job
                </h1>
            </div>

            <div className="space-y-6">
                {/* Job Introduction Section */}
                <div className="bg-white rounded-lg border border-[#EDEDED] p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM14 6H10V4H14V6Z" fill="#0B111E"/>
                            </svg>
                            <h2 className="text-lg font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Job Introduction
                            </h2>
                        </div>
                        <button
                            onClick={() => handleEdit('job-introduction')}
                            className="text-[#015FA6] hover:opacity-70"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#015FA6"/>
                            </svg>
                        </button>
                    </div>
                    <div className="flex gap-12">
                        <div className="flex flex-col gap-8">
                            <div>
                                <p className="text-sm text-[#A5A5A5] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Job Title</p>
                                <p className="text-base font-medium text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{jobData.job_title}</p>
                            </div>
                            <div>
                                <p className="text-sm text-[#A5A5A5] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Employment Type</p>
                                <p className="text-base font-medium text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {employmentTypes.join(', ')}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-[#A5A5A5] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Organization Level</p>
                                <p className="text-base font-medium text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{jobData.organization_level || 'Not specified'}</p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-8">
                            <div>
                                <p className="text-sm text-[#A5A5A5] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Location</p>
                                <p className="text-base font-medium text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {jobData.location?.country || 'Not specified'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-[#A5A5A5] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Job Category</p>
                                <p className="text-base font-medium text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{jobData.category || 'Not specified'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Organization Industry Section */}
                <div className="bg-white rounded-lg border border-[#EDEDED] p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2L2 7V9H22V7L12 2ZM4 11V13H6V11H4ZM8 11V13H10V11H8ZM4 15V17H6V15H4ZM8 15V17H10V15H8ZM12 11V13H14V11H12ZM16 11V13H18V11H16ZM12 15V17H14V15H12ZM16 15V17H18V15H16ZM2 19V21H22V19H2Z" fill="#0B111E"/>
                            </svg>
                            <h2 className="text-lg font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Organization Industry
                            </h2>
                        </div>
                        <button
                            onClick={() => handleEdit('organization-industry')}
                            className="text-[#015FA6] hover:opacity-70"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#015FA6"/>
                            </svg>
                        </button>
                    </div>
                    <div className="flex gap-2">
                        <span className="px-2 py-2 bg-[#F4F4F4] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Information
                        </span>
                        <span className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {jobData.organization_industry || 'Not specified'}
                        </span>
                    </div>
                </div>

                {/* Work Location Section */}
                <div className="bg-white rounded-lg border border-[#EDEDED] p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#0B111E"/>
                            </svg>
                            <h2 className="text-lg font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Work Location
                            </h2>
                        </div>
                        <button
                            onClick={() => handleEdit('work-location')}
                            className="text-[#015FA6] hover:opacity-70"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#015FA6"/>
                            </svg>
                        </button>
                    </div>
                    {jobData.location && jobData.location.city && jobData.location.country && (
                        <span className="inline-block px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {jobData.location.city}/{jobData.location.country}
                        </span>
                    )}
                </div>

                {/* Salary & Benefits Section */}
                {jobData.salary_range && (
                    <div className="bg-white rounded-lg border border-[#EDEDED] p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-1">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M11.8 10.9C9.53 10.31 8.8 9.7 8.8 8.75C8.8 7.66 9.81 6.9 11.5 6.9C13.28 6.9 13.94 7.75 14 9H16.21C16.14 7.28 15.09 5.7 13 5.19V3H10V5.16C8.06 5.58 6.5 6.84 6.5 8.77C6.5 11.08 8.41 12.23 11.2 12.9C13.7 13.5 14.2 14.38 14.2 15.31C14.2 16 13.71 17.1 11.5 17.1C9.44 17.1 8.63 16.18 8.52 15H6.32C6.44 17.19 8.08 18.42 10 18.83V21H13V18.85C14.95 18.48 16.5 17.35 16.5 15.3C16.5 12.46 14.07 11.49 11.8 10.9Z" fill="#0B111E"/>
                                </svg>
                                <h2 className="text-lg font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Salary & Benefits
                                </h2>
                            </div>
                            <button
                                onClick={() => handleEdit('salary')}
                                className="text-[#015FA6] hover:opacity-70"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#015FA6"/>
                                </svg>
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Information
                            </span>
                            <span className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {typeof jobData.salary_range === 'object'
                                    ? `$${jobData.salary_range.min?.toLocaleString()} USD`
                                    : jobData.salary_range}
                            </span>
                        </div>
                    </div>
                )}

                {/* Job Execution Conditions Section */}
                {(jobData.min_age || jobData.max_age || jobData.gender) && (
                    <div className="bg-white rounded-lg border border-[#EDEDED] p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-1">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z" fill="#0B111E"/>
                                </svg>
                                <h2 className="text-lg font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Job Execution Conditions
                                </h2>
                            </div>
                            <button
                                onClick={() => handleEdit('job-execution')}
                                className="text-[#015FA6] hover:opacity-70"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#015FA6"/>
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-4">
                            {(jobData.min_age || jobData.max_age) && (
                                <div className="flex gap-2">
                                    {jobData.min_age && (
                                        <span className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                            {jobData.min_age} years old
                                        </span>
                                    )}
                                    {jobData.max_age && (
                                        <span className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                            {jobData.max_age} years old
                                        </span>
                                    )}
                                </div>
                            )}
                            {jobData.gender && (
                                <div>
                                    <h3 className="text-base font-semibold text-[#0B111E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Gender
                                    </h3>
                                    <span className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        {jobData.gender}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Work Experience Section */}
                {jobData.experience_levels && jobData.experience_levels.length > 0 && (
                    <div className="bg-white rounded-lg border border-[#EDEDED] p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-1">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM14 6H10V4H14V6Z" fill="#0B111E"/>
                                </svg>
                                <h2 className="text-lg font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Work Experience
                                </h2>
                            </div>
                            <button
                                onClick={() => handleEdit('work-experience')}
                                className="text-[#015FA6] hover:opacity-70"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#015FA6"/>
                                </svg>
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {jobData.experience_levels.map((level, index) => (
                                <span key={index} className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {level}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Skills Section */}
                {(jobData.languages || jobData.software_skills || jobData.requirements?.soft_skills) && (
                    <div className="bg-white rounded-lg border border-[#EDEDED] p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-1">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2L2 7L12 12L22 7L12 2ZM2 17L12 22L22 17V11L12 16L2 11V17Z" fill="#0B111E"/>
                                </svg>
                                <h2 className="text-lg font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Skills
                                </h2>
                            </div>
                            <button
                                onClick={() => handleEdit('skills')}
                                className="text-[#015FA6] hover:opacity-70"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#015FA6"/>
                                </svg>
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {jobData.languages?.map((lang, index) => (
                                <span key={`lang-${index}`} className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {lang.name} - {lang.proficiency}
                                </span>
                            ))}
                            {jobData.software_skills?.map((software, index) => (
                                <span key={`soft-${index}`} className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {software.name} - {software.proficiency}
                                </span>
                            ))}
                            {jobData.requirements?.soft_skills?.map((skill, index) => (
                                <span key={`comm-${index}`} className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Job Description Section */}
                {jobData.job_description && (
                    <div className="bg-white rounded-lg border border-[#EDEDED] p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-1">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17Z" fill="#0B111E"/>
                                </svg>
                                <h2 className="text-lg font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Job Description
                                </h2>
                            </div>
                            <button
                                onClick={() => handleEdit('job-description')}
                                className="text-[#015FA6] hover:opacity-70"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#015FA6"/>
                                </svg>
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {jobData.working_hours && (
                                <span className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {jobData.working_hours}
                                </span>
                            )}
                            {jobData.business_trips_required && (
                                <span className="px-2 py-2 bg-[#EDEDED] text-[#0B111E] rounded-lg text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Business trips required
                                </span>
                            )}
                        </div>
                        <div className="px-3 py-2 border border-[#F4F4F4] rounded-lg">
                            <p className="text-sm text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {jobData.job_description}
                            </p>
                        </div>
                    </div>
                )}

                {/* Post Job Button */}
                <div className="flex justify-center pt-2">
                    <button
                        onClick={handlePublish}
                        disabled={isPublishing}
                        className="px-4 py-3 bg-[#015FA6] text-white text-lg font-medium rounded-lg hover:bg-[#014578] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        {isPublishing ? 'Publishing...' : 'Post Job'}
                    </button>
                </div>
            </div>
        </CompanyLayout>
    );
};

export default JobReview;
